import { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// In case throttle is required later: https://www.npmjs.com/package/throttle-debounce
import { debounce } from 'debounce';

import { QuantityOption, QuantityListType } from 'utils/quantity.util';
import { generateOptionObj, getIndex } from 'utils/quantity.util';
import { IAppState } from 'config/redux/reducers';

import { updateQuantity as updateQuantityAction } from '../redux/updateQuantity.action';
import { isUserShareMember, useAnalytic } from '@mafc/common';
import { BrazeUserAttributes } from 'pages/OrderConfirmation/utils/brazeContants';
import useNewC4Journey from 'hooks/useNewC4Journey';
import useCartBundling from 'hooks/useCartBundling';

const useQuantityUpdate = (
  {
    entryNumber,
    quantity,
    max,
    min,
    soldByWeight,
    isOffer,
    offerId,
    productId,
    intent = '',
    isSubstitute = false,
    bundleId,
  }: {
    [key: string]: any;
  },
  quantityList: QuantityListType
) => {
  const loadingEntryNumber = parseInt(`${entryNumber}${offerId ? offerId : ''}`, 10);
  const isNewC4Journey = useNewC4Journey();
  const [qty, setQty] = useState(quantity);
  const [index, setIndex] = useState(getIndex(quantityList, qty));

  const { BrazeSetCustomUserAttribute } = useAnalytic();
  const { totalUnitCount } = useSelector((state: IAppState) => state.miniCart);
  const store = useSelector((state: IAppState) => state);
  const isShareMember = isUserShareMember(store);

  const dispatch = useDispatch();
  const isCartBundlingEnabled = useCartBundling();

  const selectedValue = useMemo(() => (soldByWeight ? generateOptionObj(qty, soldByWeight) : qty), [
    qty,
    soldByWeight,
  ]);

  useEffect(() => {
    // added for cases in which api restricts the quantity
    const value = selectedValue.value ? selectedValue.value : selectedValue;
    if (value !== quantity && !isSubstitute) {
      setQty(quantity);
      setIndex(getIndex(quantityList, quantity));
    }
  }, [selectedValue, quantity, quantityList, isSubstitute]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateQuantity = useCallback(
    debounce((newQty: number, trackingData?: any) => {
      dispatch(
        updateQuantityAction(
          {
            cartEntryNumber: productId,
            qty: newQty,
            isOffer,
            offerId,
            loadingEntryNumber,
            isNewC4Journey,
            intent,
            lm: isShareMember,
            cp: isCartBundlingEnabled,
            ...(bundleId && { bundleId }),
            originalQuantity: qty,
          },
          trackingData
        )
      );
    }, 155),
    [dispatch, productId, qty]
  );

  const updateLocalQuantity = useCallback(
    (q: number, trackingData?: any) => {
      setQty(q);
      if (!isSubstitute) {
        updateQuantity(q, trackingData);
      }
    },
    [updateQuantity, isSubstitute]
  );

  const incDecQty = useCallback(
    (flag: boolean, counter: number) => {
      if (flag) {
        const nextIndex = index + counter;
        if (quantityList[nextIndex]) {
          setIndex(nextIndex);
          updateLocalQuantity(quantityList[nextIndex].value, {
            actionType: counter > 0 ? 'add' : 'remove',
            updateType: `button_${counter > 0 ? 'increment' : 'decrement'}`,
          });
        }
        // else {
        // need to discuss to log this at the time of pr
        // }
      }
    },
    [index, updateLocalQuantity, quantityList]
  );

  const onIncrementHandler = useCallback(() => incDecQty(qty < max, 1), [qty, max, incDecQty]);
  const onDecrementHandler = useCallback(() => incDecQty(qty > min, -1), [qty, min, incDecQty]);

  const onSelectHandler = useCallback(
    ({ value }: QuantityOption, nextIndex: number): void => {
      setIndex(nextIndex);
      updateLocalQuantity(value, {
        actionType: value > quantity ? 'add' : 'remove',
        updateType: `list_${value > quantity ? 'increment' : 'decrement'}`,
      });
    },
    [updateLocalQuantity, quantity]
  );

  const onDeleteHandler = useCallback((): void => {
    updateQuantity(0);
    if (totalUnitCount === 1) {
      BrazeSetCustomUserAttribute({ [BrazeUserAttributes.cart_count]: false });
    }
  }, [BrazeSetCustomUserAttribute, totalUnitCount, updateQuantity]);

  return [
    qty,
    selectedValue,
    { onIncrementHandler, onDecrementHandler, onSelectHandler, onDeleteHandler },
  ];
};

export default useQuantityUpdate;
